// src/components/tiptap/extensions/MemoryMatchNode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import CelebrationOverlay from "../../common/CelebrationOverlay.jsx";
import { supabase } from "../../../config/supabase";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";

const { FiMusic, FiSave, FiTrash2, FiSettings, FiRepeat, FiVolume2, FiVolumeX } = FiIcons;

const cls = (...xs) => xs.filter(Boolean).join(" ");
const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const SFX = {
  flip: [
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/flip.mp3",
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/flip.wav",
  ],
  correct:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/correct.mp3",
  celebration:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/celebration.mp3",
};

/* ──────────────────────────────────────────────────────────────────────────────
   PERF: per-page caches + inflight dedupe
   ──────────────────────────────────────────────────────────────────────────── */
let mmCategoriesCache = null; // string[]
let mmCategoriesInflight = null;

const mmPoolCacheByCategory = new Map(); // key: category string ("" = ALL) -> rows[]
const mmPoolInflightByCategory = new Map(); // key -> Promise

async function fetchActiveCategoriesCached() {
  if (Array.isArray(mmCategoriesCache)) return mmCategoriesCache;
  if (mmCategoriesInflight) return mmCategoriesInflight;

  mmCategoriesInflight = (async () => {
    // Only pull minimal columns
    const { data, error } = await supabase
      .from("memory_match_library")
      .select("category")
      .eq("is_active", true);

    if (error) {
      mmCategoriesInflight = null;
      return [];
    }
    const cats = Array.from(new Set((data || []).map((r) => r.category).filter(Boolean)));
    mmCategoriesCache = cats;
    mmCategoriesInflight = null;
    return cats;
  })();

  return mmCategoriesInflight;
}

async function fetchPoolCached(category) {
  const key = category ? String(category) : ""; // "" means "all active"
  if (mmPoolCacheByCategory.has(key)) return mmPoolCacheByCategory.get(key);
  if (mmPoolInflightByCategory.has(key)) return mmPoolInflightByCategory.get(key);

  const p = (async () => {
    let q = supabase
      .from("memory_match_library")
      .select("id,category,label,image_url")
      .eq("is_active", true);

    // IMPORTANT: avoid fetching the entire library unless we truly need it.
    // If no category chosen, return empty pool (forces user to choose category).
    if (!key) {
      mmPoolCacheByCategory.set(key, []);
      mmPoolInflightByCategory.delete(key);
      return [];
    }

    q = q.eq("category", key);

    const { data, error } = await q;
    const rows = error ? [] : (data || []);
    mmPoolCacheByCategory.set(key, rows);
    mmPoolInflightByCategory.delete(key);
    return rows;
  })();

  mmPoolInflightByCategory.set(key, p);
  return p;
}

/* ──────────────────────────────────────────────────────────────────────────────
   PERF: shared SFX (created once per page)
   ──────────────────────────────────────────────────────────────────────────── */
const sharedSfx = {
  ready: false,
  flip: null,
  correct: null,
  celebration: null,
  initPromise: null,
};

async function ensureSharedSfx() {
  if (sharedSfx.ready) return;
  if (sharedSfx.initPromise) return sharedSfx.initPromise;

  sharedSfx.initPromise = (async () => {
    // Pick first supported flip
    for (const url of SFX.flip) {
      try {
        const a = new Audio(url);
        a.preload = "auto";
        sharedSfx.flip = a;
        break;
      } catch {
        // ignore
      }
    }

    try {
      sharedSfx.correct = new Audio(SFX.correct);
      sharedSfx.correct.preload = "auto";
    } catch {}

    try {
      sharedSfx.celebration = new Audio(SFX.celebration);
      sharedSfx.celebration.preload = "auto";
    } catch {}

    sharedSfx.ready = true;
  })();

  return sharedSfx.initPromise;
}

function useSfx(muted) {
  useEffect(() => {
    ensureSharedSfx().catch(() => {});
  }, []);

  return (kind) => {
    if (muted) return;
    const ref =
      kind === "flip" ? sharedSfx.flip :
      kind === "correct" ? sharedSfx.correct :
      sharedSfx.celebration;

    try {
      if (!ref) return;
      ref.currentTime = 0;
      const p = ref.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      // ignore
    }
  };
}

/* helper to allow form controls to keep default behavior */
const isFormControl = (el) =>
  el && (
    el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable
  );

function MemoryMatchBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};
  const saved = !!attrs.recordId;

  const canDelete = !!ctx?.userId && !!editor?.isEditable; // teachers only

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [category, setCategory] = useState(attrs.category ?? "");
  const [cardQty, setCardQty] = useState(attrs.cardQuantity ?? 8);
  const [showLabel, setShowLabel] = useState(!!attrs.showLabel);
  const [sfxMuted, setSfxMuted] = useState(!!attrs.sfxMuted);

  const [categories, setCategories] = useState([]);
  const [pool, setPool] = useState([]);
  const [cards, setCards] = useState([]);
  const [revealed, setRevealed] = useState([]);
  const [cleared, setCleared] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const playSfx = useSfx(sfxMuted);

  // Ensure attrs exist once
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({
        noteBlockId: crypto.randomUUID?.() ?? String(Date.now()),
        cardQuantity: 8,
        showLabel: false,
        sfxMuted: false,
      });
    }
  }, []);

  // Categories (cached + deduped)
  useEffect(() => {
    let alive = true;
    (async () => {
      const cats = await fetchActiveCategoriesCached();
      if (!alive) return;
      setCategories(cats);
    })();
    return () => { alive = false; };
  }, []);

  // Pool for selected category (cached + deduped)
  useEffect(() => {
    let alive = true;
    (async () => {
      const rows = await fetchPoolCached(category);
      if (!alive) return;
      setPool(rows);
    })();
    return () => { alive = false; };
  }, [category]);

  const buildBoard = () => {
    // Guard: must have a category + pool
    if (!category) {
      alert("Please select a category first.");
      return;
    }
    if (!pool || pool.length < 4) {
      alert("Not enough cards in this category yet.");
      return;
    }

    const qty = Math.max(4, Math.min(8, Number(cardQty) || 8));
    const uniques = shuffle(pool).slice(0, qty);
    const paired = shuffle(
      uniques.concat(uniques).map((r, i) => ({
        id: `${r.id}-${i}`,
        label: r.label,
        image_url: r.image_url,
      }))
    );

    setCards(paired);
    setRevealed([]);
    setCleared(new Set());
    setDone(false);
  };

  const handleSave = async () => {
    if (!category) {
      alert("Please select a category before saving.");
      return;
    }

    const effectiveLessonNoteId = attrs.lessonNoteId ?? ctx.lessonNoteId ?? null;
    if (!effectiveLessonNoteId) return alert("Missing context: need lessonNoteId to save this tool.");

    const nbid = attrs.noteBlockId ?? (crypto.randomUUID?.() ?? String(Date.now()));
    setSaving(true);

    const { data, error } = await supabase.rpc("get_or_create_memory_match_instance", {
      p_note_block_id: nbid,
      p_school_id: attrs.schoolId ?? ctx.schoolId ?? null,
      p_lesson_note_id: effectiveLessonNoteId,
      p_created_by: ctx.userId ?? null,
      p_teacher_id: attrs.teacherId ?? ctx.userId ?? null,
      p_student_id: attrs.studentId ?? ctx.studentId ?? null,
      p_student_master_id: attrs.studentMasterId ?? ctx.masterStudentId ?? null,
      p_category: category || null,
      p_card_quantity: Number(cardQty) || 8,
      p_show_label: !!showLabel,
    });

    setSaving(false);
    if (error) return alert(error.message);

    updateAttributes({
      recordId: data.id,
      lessonNoteId: effectiveLessonNoteId,
      schoolId: attrs.schoolId ?? ctx.schoolId ?? null,
      teacherId: attrs.teacherId ?? ctx.userId ?? null,
      studentId: attrs.studentId ?? ctx.studentId ?? null,
      studentMasterId: attrs.studentMasterId ?? ctx.masterStudentId ?? null,
      noteBlockId: nbid,
      category,
      cardQuantity: Number(cardQty) || 8,
      showLabel,
      sfxMuted,
    });
  };

  const handleDelete = async () => {
    if (!attrs.recordId) return deleteNode?.();
    if (!confirm("Delete this tool from the note?")) return;

    setDeleting(true);
    const { error } = await supabase.rpc("delete_memory_match_instance", { p_id: attrs.recordId });
    setDeleting(false);
    if (error) return alert(error.message);
    deleteNode?.();
  };

  const onFlip = (idx) => {
    if (busy || revealed.includes(idx) || cleared.has(cards[idx]?.label)) return;
    playSfx("flip");

    const next = [...revealed, idx];
    setRevealed(next);

    if (next.length === 2) {
      const [a, b] = next;
      const aCard = cards[a];
      const bCard = cards[b];

      if (!aCard || !bCard) return setRevealed([]);

      if (aCard.label === bCard.label) {
        const newCleared = new Set(cleared).add(aCard.label);
        setCleared(newCleared);
        setRevealed([]);
        setTimeout(() => playSfx("correct"), 50);

        const uniqLabels = new Set(cards.map((c) => c.label));
        if (newCleared.size === uniqLabels.size) {
          setDone(true);
          setTimeout(() => playSfx("celebration"), 400);
        }
      } else {
        setBusy(true);
        setTimeout(() => {
          setRevealed([]);
          setBusy(false);
        }, 1200);
      }
    }
  };

const gridCols = useMemo(() => {
    const n = cards.length;

    // Mobile-first: fewer columns => bigger cards.
    // Desktop/tablet stays the same or better (sm+).
    if (n <= 16) return "grid-cols-3 sm:grid-cols-4";
    if (n <= 24) return "grid-cols-3 sm:grid-cols-6";
    return "grid-cols-3 sm:grid-cols-8";
  }, [cards.length]);

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
    >
      <style>{`
        .mm-card { transform-style: preserve-3d; transition: transform 0.6s; }
        .mm-card.is-flipped { transform: rotateY(180deg); }
        .mm-card-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
        .mm-card-front { transform: rotateY(180deg); }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 min-w-0">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiMusic} className="w-6 h-6" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">Memory Match</div>
            <div className="text-sm text-slate-500">A fun game to test your knowledge.</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap sm:flex-nowrap min-w-0">
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            onClick={() => {
              const next = !sfxMuted;
              setSfxMuted(next);
              updateAttributes({ sfxMuted: next });
            }}
            title={sfxMuted ? "Unmute SFX" : "Mute SFX"}
          >
            <SafeIcon icon={sfxMuted ? FiVolumeX : FiVolume2} className="w-5 h-5" />
          </button>

          <button
            className={cls(
                "px-2.5 py-1.5 sm:px-3 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 whitespace-nowrap max-w-full",
              saved
                ? "bg-slate-200 text-slate-500 border-slate-200 cursor-default"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
            )}
            disabled={saving || saved}
            onClick={handleSave}
          >
            <SafeIcon icon={FiSave} />
            {saved ? "Saved" : saving ? "Saving..." : "Save"}
          </button>

         {canDelete && (
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
            disabled={deleting}
            onClick={handleDelete}
            title="Delete Tool"
          >
            <SafeIcon icon={FiTrash2} className="w-5 h-5" />
          </button>
          )}
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="bg-white/80 rounded-xl p-4 border border-dashed">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-1">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Category</label>
              <select
                className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 transition"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select a category...</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Card Pairs</label>
              <select
                className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 transition"
                value={String(cardQty)}
                onChange={(e) => setCardQty(Number(e.target.value))}
              >
                <option value="4">4 Pairs (8 total)</option>
                <option value="6">6 Pairs (12 total)</option>
                <option value="8">8 Pairs (16 total)</option>
              </select>
            </div>

            <div className="flex items-end justify-start">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded text-orange-600 border-slate-300 focus:ring-orange-500"
                  checked={showLabel}
                  onChange={(e) => setShowLabel(e.target.checked)}
                />
                Show Labels on Cards
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              className={cls(
                "w-full md:w-auto px-6 py-2.5 rounded-xl text-base font-semibold text-white shadow-lg transition-all transform",
                saved && category ? "bg-orange-500 hover:bg-orange-600 active:scale-95" : "bg-slate-400 cursor-not-allowed"
              )}
              onClick={buildBoard}
              disabled={!saved || !category}
              title={saved ? "Start the game!" : "You must save the tool before playing"}
            >
              Let's Play!
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="p-2 sm:p-4 bg-slate-200/50 rounded-xl">
            <div className={cls("grid gap-2 sm:gap-3", gridCols)}>
              {cards.map((c, idx) => {
                const isFlipped = revealed.includes(idx) || cleared.has(c.label);
                return (
                  <div
                    key={c.id}
                    className="aspect-square"
                    style={{ perspective: "1000px" }}
                    onClick={() => onFlip(idx)}
                  >
                    <div className={cls("mm-card w-full h-full relative cursor-pointer", isFlipped && "is-flipped")}>
                      <div className="mm-card-face mm-card-front absolute w-full h-full rounded-lg shadow-md bg-white p-2 flex items-center justify-center">
                        <img
                          src={c.image_url}
                          alt={c.label}
                          className="object-contain max-w-full max-h-full pointer-events-none"
                        />
                        {showLabel && (
                          <div className="absolute bottom-1.5 text-center w-full text-[10px] font-semibold text-slate-600 bg-white/50 backdrop-blur-sm px-1 rounded">
                            {c.label}
                          </div>
                        )}
                      </div>

                      <div className="mm-card-face mm-card-back absolute w-full h-full rounded-lg shadow-inner bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white">
                        <SafeIcon icon={FiMusic} className="w-1/2 h-1/2 opacity-30" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            {done ? (
              <div className="text-base font-semibold text-green-600">
                Excellent! You matched them all!
              </div>
            ) : <div />}

            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-2"
                onClick={buildBoard}
              >
                <SafeIcon icon={FiRepeat} /> Play Again
              </button>

              <button
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-2"
                onClick={() => {
                  setCards([]);
                  setRevealed([]);
                  setCleared(new Set());
                  setDone(false);
                }}
              >
                <SafeIcon icon={FiSettings} /> Settings
              </button>
            </div>
          </div>
        </>
      )}

      <CelebrationOverlay
        show={done}
        message="You're a Memory Master!"
        duration={3200}
        fullScreen
        intensity="epic"
        onDone={() => setDone(false)}
      />
    </NodeViewWrapper>
  );
}

const MemoryMatchNode = Node.create({
  name: "memoryMatch",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      schoolId: { default: null },
      teacherId: { default: null },
      studentId: { default: null },
      studentMasterId: { default: null },
      recordId: { default: null },
      category: { default: null },
      cardQuantity: { default: 8 },
      showLabel: { default: false },
      sfxMuted: { default: false },
    };
  },
  parseHTML() { return [{ tag: "memory-match" }]; },
  renderHTML({ HTMLAttributes }) { return ["memory-match", mergeAttributes(HTMLAttributes)]; },
  addNodeView() { return ReactNodeViewRenderer(MemoryMatchBlock); },
});

export default MemoryMatchNode;